import type { ComputedRef } from 'vue'
import type { BadgeCustomerBrandProps } from '../types/interfaces'

import { CustomerBrand } from '@shared/config'

import brighterlyMark from '../images/brighterly.svg'
import smartestMindsMark from '../images/smartestminds.svg'

interface BadgeCustomerBrandOptions {
  props: BadgeCustomerBrandProps
}

interface BadgeCustomerBrandReturn {
  brandKey: ComputedRef<CustomerBrand | null>
  label: ComputedRef<string>
  modifier: ComputedRef<string>
  mark: ComputedRef<string>
  isIcon: ComputedRef<boolean>
  isVisible: ComputedRef<boolean>
}

const BRAND_LABEL: Record<CustomerBrand, string> = {
  [CustomerBrand.Brighterly]: 'Brighterly',
  [CustomerBrand.SmartestMinds]: 'SmartestMinds',
}

const BRAND_MARK: Record<CustomerBrand, string> = {
  [CustomerBrand.Brighterly]: brighterlyMark,
  [CustomerBrand.SmartestMinds]: smartestMindsMark,
}

const BRAND_MODIFIER: Record<CustomerBrand, string> = {
  [CustomerBrand.Brighterly]: 'brighterly',
  [CustomerBrand.SmartestMinds]: 'smartestminds',
}

const isKnownBrand = (brand: unknown): brand is CustomerBrand => (
  Object.values(CustomerBrand).includes(brand as CustomerBrand)
)

export const useBadgeCustomerBrand = ({ props }: BadgeCustomerBrandOptions): BadgeCustomerBrandReturn => {
  const brandKey = computed<CustomerBrand | null>(() => (
    isKnownBrand(props.brand) ? props.brand : null
  ))
  const label = computed<string>(() => (brandKey.value ? BRAND_LABEL[brandKey.value] : ''))
  const modifier = computed<string>(() => (brandKey.value ? BRAND_MODIFIER[brandKey.value] : ''))

  const mark = computed<string>(() => (brandKey.value ? BRAND_MARK[brandKey.value] : ''))
  const isIcon = computed<boolean>(() => props.view === 'icon')

  const isVisible = computed<boolean>(() => (
    !!brandKey.value && (!isIcon.value || brandKey.value === CustomerBrand.SmartestMinds)
  ))

  return {
    brandKey,
    label,
    modifier,
    mark,
    isIcon,
    isVisible,
  }
}
